import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Alert from '../components/Alert.jsx';
import Loader from '../components/Loader.jsx';
import { useFlash } from '../context/ToastContext.jsx';
import { api } from '../api/client.js';

// /access/:packageSlug — per-package access page.
// Shows every overlay in the package with one of three states:
//   • granted   — checked + disabled
//   • pending   — unchecked + disabled, "Pending" badge
//   • available — checkbox enabled, click to add to the batch
// User clicks "Submit requests" to send the batch in one POST.
//
// Reachable from:
//   • Dashboard owned-card "Request access for N more overlays" link
//   • Dashboard ghost-card "Request Access" button
//   • Direct URL
//
// Admins land on the same page (consistent UI), but every overlay shows up
// as 'granted' since admins implicitly hold every grant — the matrix becomes
// view-only. Admins do their grant work from the existing UserEdit page.
export default function Access() {
  const { packageSlug } = useParams();

  const [packages, setPackages] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const flash = useFlash();

  // Design-request panel state.
  const [designOpen, setDesignOpen] = useState(false);
  const [designName, setDesignName] = useState('');
  const [designDescription, setDesignDescription] = useState('');
  const [designBaseSlug, setDesignBaseSlug] = useState('');
  const [designSubmitting, setDesignSubmitting] = useState(false);
  const [myDesignRequests, setMyDesignRequests] = useState(null);

  const loadState = () =>
    api.accessState()
      .then((data) => {
        setPackages(data?.packages ?? []);
        setViewer(data?.viewer ?? null);
        setSelected(new Set());
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    let alive = true;
    api.accessState()
      .then((data) => {
        if (!alive) return;
        setPackages(data?.packages ?? []);
        setViewer(data?.viewer ?? null);
      })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  // Pull the user's existing design requests so we can show their pending
  // ones for this package.
  useEffect(() => {
    let alive = true;
    api.listMyDesignRequests()
      .then((data) => { if (alive) setMyDesignRequests(data?.requests ?? []); })
      .catch(() => { if (alive) setMyDesignRequests([]); });
    return () => { alive = false; };
  }, []);

  const pkg = useMemo(
    () => (packages ?? []).find((p) => p.slug === packageSlug) ?? null,
    [packages, packageSlug],
  );

  const counts = useMemo(() => {
    if (!pkg) return { granted: 0, pending: 0, available: 0, total: 0 };
    const c = { granted: 0, pending: 0, available: 0, total: pkg.overlays.length };
    for (const o of pkg.overlays) c[o.status] = (c[o.status] || 0) + 1;
    return c;
  }, [pkg]);

  function toggle(overlayId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(overlayId)) next.delete(overlayId);
      else next.add(overlayId);
      return next;
    });
  }

  function selectAllAvailable() {
    if (!pkg) return;
    setSelected(new Set(pkg.overlays.filter((o) => o.status === 'available').map((o) => o.id)));
  }

  async function submit() {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.requestAccess([...selected]);
      const created = res?.created?.length ?? 0;
      const skippedPending = res?.skipped?.pending?.length ?? 0;
      const parts = [];
      if (created > 0) parts.push(`${created} request${created === 1 ? '' : 's'} submitted`);
      if (skippedPending > 0) parts.push(`${skippedPending} already pending`);
      flash({ kind: 'success', msg: parts.join(' · ') || 'Nothing to submit.' });
      await loadState();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Refresh the design-request list (after a successful submit).
  function refreshDesignRequests() {
    return api.listMyDesignRequests()
      .then((data) => setMyDesignRequests(data?.requests ?? []))
      .catch(() => {});
  }

  async function submitDesignRequest(e) {
    e.preventDefault();
    if (!designName.trim() || !designDescription.trim()) return;
    setDesignSubmitting(true);
    setError(null);
    try {
      await api.createDesignRequest({
        packageSlug,
        baseOverlaySlug: designBaseSlug || null,
        requestedName: designName.trim(),
        description: designDescription.trim(),
      });
      setDesignName('');
      setDesignDescription('');
      setDesignBaseSlug('');
      setDesignOpen(false);
      flash({ kind: 'success', msg: 'Design request submitted — admin will review.' });
      await refreshDesignRequests();
    } catch (e) {
      setError(e.message);
    } finally {
      setDesignSubmitting(false);
    }
  }

  if (error) {
    return (
      <div className="row justify-content-center mt-5">
        <div className="col-md-8">
          <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>
          <Link to="/" className="btn btn-outline-secondary btn-sm">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (packages === null) {
    return <Loader label="Loading…" />;
  }

  if (!pkg) {
    return (
      <div className="row justify-content-center mt-5">
        <div className="col-md-8">
          <Alert kind="warning">
            Package <code>{packageSlug}</code> not found.{' '}
            <Link to="/">Back to dashboard →</Link>
          </Alert>
        </div>
      </div>
    );
  }

  const heroBg = `url(/api/packages/${pkg.slug}/art/card)`;

  return (
    <div className="row justify-content-center mt-4">
      <div className="col-lg-9 col-xl-7">
        <div className="mb-3">
          <Link to="/" className="btn btn-outline-secondary btn-sm">
            ← Back to Dashboard
          </Link>
        </div>

        <div className="card" style={{ borderColor: 'var(--neon-blue)' }}>
          <div
            className="card-header"
            aria-label={pkg.name}
            style={{
              backgroundColor: '#0e0e0e',
              backgroundImage: heroBg,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: 140,
            }}
          />
          <div className="card-body">
            <h4 className="mb-1" style={{ color: 'var(--neon-blue)' }}>{pkg.name}</h4>
            {pkg.description && (
              <p className="text-muted small mb-3">{pkg.description}</p>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className="badge bg-success">{counts.granted} granted</span>
              {counts.pending > 0 && (
                <span className="badge bg-warning text-dark">{counts.pending} pending</span>
              )}
              <span className="badge bg-secondary">{counts.available} available</span>
            </div>


            {pkg.overlays.length === 0 ? (
              <p className="text-muted small mb-0">
                This package has no overlays defined yet.
              </p>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Overlays</span>
                  {counts.available > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={selectAllAvailable}
                      disabled={submitting}
                    >
                      Select all available
                    </button>
                  )}
                </div>

                <ul className="list-group list-group-flush mb-3">
                  {pkg.overlays.map((o) => {
                    const isSelectable = o.status === 'available';
                    const isSelected = selected.has(o.id);
                    return (
                      <li
                        key={o.id}
                        className="list-group-item bg-transparent d-flex align-items-center"
                        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                      >
                        <div className="form-check flex-grow-1 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`overlay-${o.id}`}
                            checked={o.status === 'granted' || isSelected}
                            disabled={!isSelectable || submitting}
                            onChange={() => toggle(o.id)}
                          />
                          <label className="form-check-label" htmlFor={`overlay-${o.id}`}>
                            {o.name}
                          </label>
                        </div>
                        {o.status === 'granted' && (
                          <span className="badge bg-success">✓ Granted</span>
                        )}
                        {o.status === 'pending' && (
                          <span className="badge bg-warning text-dark">⏳ Pending</span>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  disabled={selected.size === 0 || submitting}
                  onClick={submit}
                >
                  {submitting
                    ? 'Submitting…'
                    : selected.size === 0
                      ? 'Pick at least one overlay'
                      : `Submit ${selected.size} request${selected.size === 1 ? '' : 's'}`}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Design-request panel: ask for a custom overlay variant ──
            Entirely hidden when the admin has disabled design requests
            for this user — no badges, no copy, no list. The /my-requests
            page still surfaces any pre-existing history. */}
        {!viewer?.designRequestsDisabled && (
        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div>Want a custom overlay for <strong>{pkg.name}</strong>?</div>
              <div className="text-muted small">
                Submit a build request — the admin reviews each one before
                a private overlay is built and granted to you.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setDesignOpen((v) => !v)}
            >
              {designOpen ? 'Cancel' : '+ Request Custom Overlay'}
            </button>
          </div>
          {designOpen && (
            <div className="card-body">
              <p className="text-muted small mb-3">
                Describe the custom overlay you'd like built for
                <strong> {pkg.name}</strong>. The admin will review and build a
                private version just for you — fulfilment auto-grants you access.
              </p>
              <form onSubmit={submitDesignRequest}>
                <div className="mb-2">
                  <label className="form-label">Variant of</label>
                  <select
                    className="form-select"
                    value={designBaseSlug}
                    onChange={(e) => setDesignBaseSlug(e.target.value)}
                    disabled={designSubmitting}
                  >
                    <option value="">— New / not based on an existing overlay —</option>
                    {pkg.overlays.map((o) => (
                      <option key={o.id} value={o.slug}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={designName}
                    onChange={(e) => setDesignName(e.target.value)}
                    placeholder="e.g. Starting Soon — sponsor banner edition"
                    disabled={designSubmitting}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={designDescription}
                    onChange={(e) => setDesignDescription(e.target.value)}
                    placeholder="What should it look like? Any new fields you want it to support?"
                    disabled={designSubmitting}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={
                    designSubmitting
                    || !designName.trim()
                    || !designDescription.trim()
                  }
                >
                  {designSubmitting ? 'Submitting…' : 'Submit Design Request'}
                </button>
              </form>
            </div>
          )}
          {/* My design requests for this package */}
          {(() => {
            const mine = (myDesignRequests ?? []).filter(
              (r) => r.package?.slug === packageSlug
            );
            if (mine.length === 0) return null;
            return (
              <ul className="list-group list-group-flush">
                {mine.map((r) => (
                  <li
                    key={r.id}
                    className="list-group-item bg-transparent d-flex justify-content-between align-items-center"
                    style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <div>
                      <div>{r.requestedName}</div>
                      <div className="text-muted small">
                        {new Date(r.requestedAt).toLocaleString()}
                        {r.baseOverlay && <> · variant of {r.baseOverlay.name}</>}
                      </div>
                    </div>
                    <span className={`badge ${
                      r.status === 'pending' ? 'bg-secondary'
                      : r.status === 'in_progress' ? 'bg-warning text-dark'
                      : r.status === 'fulfilled' ? 'bg-success'
                      : 'bg-danger'
                    }`}>
                      {r.status === 'in_progress' ? 'in progress' : r.status}
                    </span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
        )}
      </div>
    </div>
  );
}
